import Config from 'Config';
import { SubmissionError } from 'redux-form';
import { CALL_API, processErrorResponse, getDefaultRequestHeaders } from '../common/middleware/api';
import { fetchFranchisorSettings } from '../dashboard/settings/franchisor/actions';
import { loadAdHocMessageTemplates } from '../dashboard/messageTemplates/actions';
import browserStorage from '../common/services/browserStorage';
import authUtils from './utils';

export const LOGIN_REQUEST = 'LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'LOGIN_SUCCESS';
export const LOGIN_FAILURE = 'LOGIN_FAILURE';

export const REFRESH_TOKEN_REQUEST = 'REFRESH_TOKEN_REQUEST';
export const REFRESH_TOKEN_SUCCESS = 'REFRESH_TOKEN_SUCCESS';
export const REFRESH_TOKEN_FAILURE = 'REFRESH_TOKEN_FAILURE';

export const CURRENT_USER_REQUEST = 'CURRENT_USER_REQUEST';
export const CURRENT_USER_SUCCESS = 'CURRENT_USER_SUCCESS';
export const CURRENT_USER_FAILURE = 'CURRENT_USER_FAILURE';


export const RESET_PASSWORD_REQUEST = 'RESET_PASSWORD_REQUEST';
export const RESET_PASSWORD_SUCCESS = 'RESET_PASSWORD_SUCCESS';
export const RESET_PASSWORD_FAILURE = 'RESET_PASSWORD_FAILURE';

export const FORGOTTEN_PASSWORD_REQUEST = 'FORGOTTEN_PASSWORD_REQUEST';
export const FORGOTTEN_PASSWORD_SUCCESS = 'FORGOTTEN_PASSWORD_SUCCESS';
export const FORGOTTEN_PASSWORD_FAILURE = 'FORGOTTEN_PASSWORD_FAILURE';

export const LOGOUT = 'LOGOUT';

const TOKEN_URL = `${Config.authServerUrl}/token`;
const CURRENT_USER_URL = `${Config.backofficeApiUrl}/api/account`;

const requestLogin = (credentials) => ({
	type: LOGIN_REQUEST,
	payload: credentials
});

const receiveLogin = (json) => ({
	type: LOGIN_SUCCESS,
	payload: {
		accessToken: json.access_token,
		refreshToken: json.refresh_token
	}
});

const loginError = (errorMessage) => ({
	type: LOGIN_FAILURE,
	payload: { error: errorMessage }
});

export const loginUser = (credentials) => (dispatch, getState) => {
	const state = getState();
	const { username, password } = credentials;

	const config = {
		method: 'POST',
		headers: {
			...getDefaultRequestHeaders(state),
			'Content-Type': 'application/x-www-form-urlencoded'
		},
		body: `grant_type=password&username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&client_id=back_office&scope=back_office_api%20offline_access`
	};

	dispatch(requestLogin(credentials));
	return fetch(TOKEN_URL, config)
		.then(response => response.json().then(json => {
			if (!response.ok) {
				const errorMessage = json.error_description ? json.error_description : json.error;
				dispatch(loginError(errorMessage));
				throw new SubmissionError({			// handled by redux-form
					_error: errorMessage
				});
			} else {
				browserStorage.setToLocalStorage(state.configuration.franchisor, 'accessToken', json.access_token);
				browserStorage.setToLocalStorage(state.configuration.franchisor, 'refreshToken', json.refresh_token);

				dispatch(receiveLogin(json));
				dispatch(getCurrentUser(json.access_token));
			//	if (authUtils.hasPermission(state.currentUser, 'FranchisorSettings')) {
				//		dispatch(fetchFranchisorSettings(getState().configuration.franchisor.id));//load in getCurrentUser
			//	}
			//	if (authUtils.hasPermission(state.currentUser, 'MessageTemplates')) {
				//		dispatch(loadAdHocMessageTemplates());//load in getCurrentUser
			//	}			
			}
		}));
};

const requestRefreshToken = (initializing) => ({
	type: REFRESH_TOKEN_REQUEST,
	payload: { initializing }
});

const receiveRefreshToken = (json) => ({
	type: REFRESH_TOKEN_SUCCESS,
	payload: {
		accessToken: json.access_token,
		refreshToken: json.refresh_token
	}
});

const refreshTokenError = (errorMessage) => ({
	type: REFRESH_TOKEN_FAILURE,
	payload: { error: errorMessage }
});

let isRefreshingToken = false;
let refreshTokenPromise = null;

const sleep = (ms) => {
	return new Promise(resolve => setTimeout(resolve, ms));
};

export const refreshToken = (initializing) => (dispatch, getState) => {
	if (!isRefreshingToken) {
		const state = getState();
		const refreshToken = browserStorage.getFromLocalStorage(state.configuration.franchisor, 'refreshToken');

		if (!refreshToken) {
			const errorMessage = 'Refresh token is missing';
			dispatch(logoutUser());
			dispatch(refreshTokenError(errorMessage));
			return Promise.resolve(false);
		}

		isRefreshingToken = true;

		const config = {
			method: 'POST',
			headers: {
				...getDefaultRequestHeaders(state),
				'Content-Type': 'application/x-www-form-urlencoded'
			},
			body: `grant_type=refresh_token&refresh_token=${encodeURIComponent(refreshToken)}&client_id=back_office`
		};

		dispatch(requestRefreshToken(initializing));

		refreshTokenPromise = fetch(TOKEN_URL, config)
			.then(response => {
				isRefreshingToken = false;
				return response.json().then(json => {
					if (!response.ok) {
						dispatch(logoutUser());
						dispatch(refreshTokenError(json.error_description ? json.error_description : json.error));
						return Promise.resolve(false);
					} else {
						browserStorage.setToLocalStorage(state.configuration.franchisor, 'accessToken', json.access_token);
						browserStorage.setToLocalStorage(state.configuration.franchisor, 'refreshToken', json.refresh_token);

						dispatch(receiveRefreshToken(json));
						dispatch(getCurrentUser(json.access_token));
						//if (authUtils.hasPermission(state.currentUser, 'MessageTemplates')) {
						//	dispatch(loadAdHocMessageTemplates());//load in getCurrentUser
						//}
						return Promise.resolve(true);
					}
				});
			}).catch((error) => {
				isRefreshingToken = false;
				return Promise.resolve(false);
			});
	}

	return refreshTokenPromise;
};

const requestGetCurrentUser = () => ({
	type: CURRENT_USER_REQUEST
});

const receiveGetCurrentUser = (user) => ({
	type: CURRENT_USER_SUCCESS,
	payload: user
});

const getCurrentUserError = (errorMessage) => ({
	type: CURRENT_USER_FAILURE,
	payload: { error: errorMessage }
});

export const getCurrentUser = (accessToken) => (dispatch, getState) => {
	const config = {
		method: 'GET',
		headers: {
			...getDefaultRequestHeaders(getState()),
			'Authorization': 'Bearer ' + accessToken
		}
	};
	dispatch(requestGetCurrentUser());
	return fetch(CURRENT_USER_URL, config)
		.then(response => response.json().then(json => {
			if (!response.ok) {
				const errors = processErrorResponse(json);
				dispatch(getCurrentUserError(errors.message));
			} else {
				dispatch(receiveGetCurrentUser(json));
				if (authUtils.hasPermission(json, 'FranchisorSettings')) {
					dispatch(fetchFranchisorSettings(getState().configuration.franchisor.id));
				}
				if (authUtils.hasPermission(json, 'MessageTemplates')) {
					dispatch(loadAdHocMessageTemplates());
				}
			}
		}));
};

export const resetPassword = (credentials, history) => ({
	[CALL_API]: {
		types: [RESET_PASSWORD_REQUEST, RESET_PASSWORD_SUCCESS, RESET_PASSWORD_FAILURE],
		endpoint: `api/passwordReset`,
		method: 'PUT',
		request: credentials,
		form: 'resetPassword',
		transition: {
			history,
			successPath: '/login'
		}
	}
});

export const forgottenPassword = (username, history) => ({
	[CALL_API]: {
		types: [FORGOTTEN_PASSWORD_REQUEST, FORGOTTEN_PASSWORD_SUCCESS, FORGOTTEN_PASSWORD_FAILURE],
		endpoint: `api/passwordReset`,
		method: 'POST',
		request: username,
		form: 'forgottenPassword',
		transition: {
			history,
			successPath: (response) => {
				return `/forgottenPassword?email=${response.email}`;
			}
		}
	}
});

export const logoutUser = () => (dispatch, getState) => {
	const { configuration } = getState();
	browserStorage.removeFromLocalStorage(configuration.franchisor, 'accessToken');
	browserStorage.removeFromLocalStorage(configuration.franchisor, 'refreshToken');

	dispatch({
		type: LOGOUT
	});
}